import { giphyLightCharcoal } from '@giphy/js-brand'
import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { BORDER_RADIUS_MEDIUM, UPLOAD_BUTTON_LABEL } from '../../constants'
import { CreationToolsContext } from '../../manager'
import CreationToolOutputType from '../../types/output-type'
import CreationToolStatus from '../../types/status'
import CreationToolUserRestriction from '../../types/user-restriction'
import UploadButtonComponent from '../upload-button'
import { CreationToolUploadContext } from './manager'

const Tooltip = styled.div`
    background: #fff;
    border-radius: ${BORDER_RADIUS_MEDIUM}px;
    bottom: 100%;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
    box-sizing: border-box;
    color: ${giphyLightCharcoal};
    display: none;
    font-size: 15px;
    font-weight: normal;
    margin-bottom: 3px;
    padding: 20px;
    position: absolute;
    left: 50%;
    max-width: 220px;
    text-align: center;
    transform: translateX(-50%);

    &:after {
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-top: 6px solid #fff;
        content: '';
        height: 0;
        left: 50%;
        margin-left: -6px;
        position: absolute;
        top: 100%;
        width: 0;
    }
`

const Wrapper = styled.div`
    position: relative;

    &:hover ${Tooltip} {
        display: block;
    }
`

const Button = styled(UploadButtonComponent)`
    box-sizing: content-box;
    margin: 0 -10px;

    i {
        font-size: 16px;
        font-weight: normal;
        margin-left: 5px;
        margin-top: 2px;
    }
`

const CreationToolUploadButton = () => {
    const { userRestrictionAccess } = useContext(CreationToolsContext)
    const { upload, uploadSources, pause, status, username } = useContext(CreationToolUploadContext)
    const [canUpload, setCanUpload] = useState<boolean>(false)
    const [tooltipText, setTooltipText] = useState<string>('')
    const isLoggedIn = userRestrictionAccess.indexOf(CreationToolUserRestriction.LoggedInOnly) > -1
    const successfulUploadCount = uploadSources.filter(src => !!src.uploadedId).length
    const showChannelButton = username && uploadSources.length > 1 && successfulUploadCount === uploadSources.length
    const total = uploadSources.filter(source => !source.error).length
    const current = Math.min(uploadSources.filter(source => !!source.uploadedId).length + 1, total)
    const statusLabel = CreationToolStatus.label(status)
    const isUploading = status !== CreationToolStatus.Idle

    useEffect(() => {
        if (!isLoggedIn) {
            setTooltipText('You must be logged in to upload.')
            setCanUpload(false)
            return
        }

        if (status !== CreationToolStatus.Idle) {
            setTooltipText('')
            setCanUpload(false)
            return
        }

        const readySources = uploadSources.filter(
            source => !source.error && !source.uploadedId && source.status !== CreationToolStatus.ConvertingToSticker
        )
        const needTitles = readySources.filter(
            source => CreationToolOutputType.requiresTitle(source.outputType) && !source.title
        )

        if (needTitles.length > 0) {
            if (needTitles.length === 1) {
                setTooltipText(`1 item requires a title before uploading.`)
            } else {
                setTooltipText(`${needTitles.length} items require titles before uploading.`)
            }
            setCanUpload(false)
            return
        }

        setTooltipText('')
        setCanUpload(readySources.length > 0)
    }, [uploadSources, status, isLoggedIn, setCanUpload, setTooltipText])

    const getButtonLabel = (): string => {
        if (isUploading) {
            return `${statusLabel}${total > 1 ? ` ${current} of ${total}` : ``}`
        }
        if (showChannelButton) {
            return `Open Channel`
        }
        return UPLOAD_BUTTON_LABEL
    }

    return (
        <Wrapper>
            <Button
                {...{
                    isUploading,
                    disabled: !showChannelButton && !canUpload,
                    progress: isUploading ? current / total : 0,
                    url: showChannelButton ? `/channel/${username}` : '',
                    onPause: total > 1 ? pause : undefined,
                    onClick: showChannelButton ? undefined : () => upload(uploadSources),
                }}
            >
                {getButtonLabel()}
                {tooltipText && <i className="ss-info" />}
            </Button>
            {tooltipText && <Tooltip>{tooltipText}</Tooltip>}
        </Wrapper>
    )
}

export default CreationToolUploadButton
